import { AfterContentInit, Component, OnDestroy, OnInit } from '@angular/core';
import { Feature } from 'ol';
import Point from 'ol/geom/Point';
import { transform } from 'ol/proj';
import { Icon, Style } from 'ol/style';
import { GoogleStreetViewService } from '../../services/google-street-view.service';
import { MapService } from '../../services/map.service';
import { firstValueFrom } from 'rxjs';

/**
 * Component to display the google Street View menu
 */
@Component({
  selector: 'ebw-google-streetview',
  templateUrl: './google-streetview.component.html',
  styleUrls: ['./google-streetview.component.scss']
})
export class GoogleStreetviewComponent implements OnInit, AfterContentInit, OnDestroy {
  // listener objects to keep track of the listeners on the map, to destroy them OnDestroy
  positionChangedPanoramaListener: google.maps.MapsEventListener;
  povChangedPanoramaListener: google.maps.MapsEventListener;

  // feature to display the icon to show the position of the user in the map
  streetviewMarker: Feature;

  // service for google streetview, from google
  googleStreetViewService: google.maps.StreetViewService;

  // flag to keep track of whether some data was found and the set point
  noData: boolean = true;

  // flag to keep track of whether the streetview has been initialized
  firstRender: boolean = true;

  // array to keep track of the coordinates of the last click
  lastClick: number[] = [];

  // inject the necessary services
  constructor(
    private _mapService: MapService,
    private _streetviewService: GoogleStreetViewService
  ) { }

  /**
   * init function, called upon the creation of the component
   */
  async ngOnInit() {
    // initialize the google street view service
    this.googleStreetViewService = new google.maps.StreetViewService();
    // bind the map click to the adjust view position function
    console.log(this._mapService.openLayersMap);

    if (await firstValueFrom(this._mapService.mapConfigurationLoaded$)) {
      this._mapService.openLayersMap.on('click', this._adjustStreetViewPosition);
    }
  }

  /**
   * function called after the content of the component is initialized
   */
  ngAfterContentInit(): void {
    // initialize the street view object
    if (!this._streetviewService.streetViewPanorama) {
      this._initStreetView();
    }
  }

  /**
   * function called when the component is destroyed
   */
  ngOnDestroy(): void {
    // remove the google maps listeners if they exist
    if (this.positionChangedPanoramaListener) {
      this.positionChangedPanoramaListener.remove();
    }
    if (this.povChangedPanoramaListener) {
      this.povChangedPanoramaListener.remove();
    }

    // unbind the function from the click event of the map
    this._mapService.openLayersMap.un('click', this._adjustStreetViewPosition);
    // set the steet view object inside the google map to null
    this._mapService.googleMapsMap.googleMap.setStreetView(null);
    // set the street view panorama inside the street view service to null
    this._streetviewService.streetViewPanorama = null;
    // remove the map marker
    this._mapService.removeMarker(this._streetviewService.STREET_VIEW_MARKER_TITLE);

    console.log("destroyed google street view");
  }

  /**
   * function to initialize the street view object
   * @param {number[]} coords array di due numeri opzionale, contiene cordinate in EPSG:3857. altrimenti usa il centro della mappa
   * @returns 
   */
  async _initStreetView(coords: number[] = undefined) {
    // if some coords were passed, convert them to 4326
    if (coords) {
      coords = transform(coords, MapService.EPSG_3857_KEY, MapService.EPSG_4326_KEY);
    } else {
      coords = this._mapService.mapCenter;
    }

    // check if the selected spot is available to initialize the street view
    if (!await this._checkAvailabilityWrapper(coords).then(value => value).catch(error => error)) {
      return;
    }

    // get a reference to the streetview component in the template
    let streetviewElement = document.getElementById("streetview") as HTMLElement;

    // initialize the street view object
    this._streetviewService.streetViewPanorama = new google.maps.StreetViewPanorama(
      streetviewElement,
      {
        position: { lat:coords[1], lng:coords[0] },
        pov: {
          heading: 34,
          pitch: 10,
        },
        fullscreenControl: false,
        panControl: false,
        zoomControl: false,
        enableCloseButton: false,
        fullscreenControlOptions: {
          position: google.maps.ControlPosition.BOTTOM_CENTER,
        },
        addressControlOptions: {
          position: google.maps.ControlPosition.BOTTOM_LEFT,
        },
      }
    );   

    //Init the position marker
    let markerInitialPosition = this._mapService.openLayersMap.getView().getCenter();
    markerInitialPosition = transform(
      markerInitialPosition,
      MapService.EPSG_3857_KEY,
      MapService.EPSG_4326_KEY
    );
    let markerStyle = new Style({
      image: new Icon({
        src: "assets/icons/arrow.png", 
        rotation: 180 * (Math.PI / 180),
        scale: 0.05,
      }),
    });

    // Listener for the change of position inside Google Street View
    this.positionChangedPanoramaListener = google.maps.event.addListener(
      this._streetviewService.streetViewPanorama,
      "position_changed",
      () => {
        //Sposto il centro della mappa --> per cui, per la funzione registrata, aggiorno il segnaposto
        var newLonLat = transform(
          [
            this._streetviewService.streetViewPanorama.getPosition().lng(),
            this._streetviewService.streetViewPanorama.getPosition().lat(),
          ],
          MapService.EPSG_4326_KEY,
          MapService.EPSG_3857_KEY
        );

        // set the new center for the map to the position of the street view
        this._mapService.openLayersMap.getView().setCenter(newLonLat);

        // update the marker
        this.streetviewMarker.setGeometry(new Point(newLonLat));
        this.streetviewMarker.setStyle(
          new Style({
            image: new Icon({
              src: "assets/icons/arrow.png",
              rotation:
                ((180 + this._streetviewService.streetViewPanorama.getPov().heading) % 360) *
                (Math.PI / 180), //180 * (Math.PI/180),
                scale: 0.05,
            }),
          })
        );
      }
    );

    // Listener per il cambio di orientamento dentro SV (mi giro destra/sinistra)
    this.povChangedPanoramaListener = google.maps.event.addListener(
      this._streetviewService.streetViewPanorama,
      "pov_changed",
      () => {
        //Ruoto il marker sulla mappa
        if (this.streetviewMarker != null) { 
          this.streetviewMarker.setStyle(
            new Style({
              image: new Icon({
                src: "assets/icons/arrow.png",
                rotation:
                  ((180 + this._streetviewService.streetViewPanorama.getPov().heading) % 360) *
                  (Math.PI / 180), //180 * (Math.PI/180),
                  scale: 0.05,
              }),
            })
          );
        }
      }
    );

    // this._mapService.googleMapsMap.googleMap.setStreetView(this._streetviewService.streetViewPanorama);
    this._streetviewService.ready = true;

    //Add marker to map
    if (!this.streetviewMarker) {
      this._mapService.addMarker(this._streetviewService.STREET_VIEW_MARKER_TITLE, markerInitialPosition, markerStyle);
      this.streetviewMarker = this._mapService.mapMarkers.get(this._streetviewService.STREET_VIEW_MARKER_TITLE);
    }

    // notify that there's some data available for the street view
    this.noData = false;
  }

  /**
   * function to pass as callback when there's a click on the map
   * @param {Object} event event object containing the coordinates of the click on the map 
   * @returns 
   */
  _adjustStreetViewPosition = async (event) => {
    // extract the coordinates of the click and store them
    let coordinate = event.coordinate;
    this.lastClick = coordinate;

    // check that the service is initialized properly
    if (!this.googleStreetViewService) {
      return;
    }

    // if the google street view object isn't initialized yet
    if (!this._streetviewService.streetViewPanorama) {
      // try to initialize it
      await this._initStreetView(coordinate);
      // notify that this is not the first render anymore
      this.firstRender = false;

      // if the street view is still not initialized (wrong position to initialize)
      if (!this._streetviewService.streetViewPanorama) {
        // update the street view marker, create a new one if it doesn't exist yet
        if (!this.streetviewMarker) {
          this._mapService.addMarker(this._streetviewService.STREET_VIEW_MARKER_TITLE, this.lastClick, new Style({
            image: new Icon({
              src: "assets/icons/cross.png",
              scale: 0.05,
            }),
          }));
          this.streetviewMarker = this._mapService.mapMarkers.get(this._streetviewService.STREET_VIEW_MARKER_TITLE);
          this.streetviewMarker.setGeometry(new Point(this.lastClick));
        } else {
          this.streetviewMarker.setGeometry(new Point(this.lastClick));
          this.streetviewMarker.setStyle(new Style({
            image: new Icon({
              src: "assets/icons/cross.png",
              scale: 0.05,
            }),
          }));
        }
        // notify that there's no data for the street view
        this.noData = true;

        return;
      }
    }

    // notify that this is not the first render anymore
    this.firstRender = false;

    // calculate the correct projection of the click coordinates to pass it to google street view
    let projectedCoords = transform(coordinate, MapService.EPSG_3857_KEY, MapService.EPSG_4326_KEY);

    // try to get data at the point clicked with a radius that varies with the zoom level
    this.googleStreetViewService.getPanorama(
      {
        location: new google.maps.LatLng(projectedCoords[1], projectedCoords[0]),
        radius: this._getRadius(),
      },
      // callback function to handle the response data
      this._processSVData
    )
  }

  /**
   * callback function called when trying to get street view data on click
   * @param {Object} data output data containing the street view info 
   * @param {enum} status string showing the status of the operation 
   */
  _processSVData = (data, status) => {
    // if everything went fine and we found something
    if (status == google.maps.StreetViewStatus.OK) {
      // notify that we have data
      this.noData = false;
      // make the street view component visible
      this._streetviewService.streetViewPanorama.setVisible(true);
      // set the street view to the data retrieved
      this._streetviewService.streetViewPanorama.setPano(data.location.pano);
      // set the Point Of View of the new view
      this._streetviewService.streetViewPanorama.setPov({
        heading: this._streetviewService.streetViewPanorama.getPov().heading, //mantengo precedente orientazione
        pitch: 0,
      });

      // update the position of the map marker
      var lat = data.location.latLng.lat();
      var lng = data.location.latLng.lng();

      if (this.streetviewMarker != null) {
        var streetViewPosition = transform(
          [lng, lat],
          MapService.EPSG_4326_KEY,
          MapService.EPSG_3857_KEY
        );
        this.streetviewMarker.setGeometry(new Point(streetViewPosition));
      }
      
    }
    // if there was an error or no valid data was found
    else {
      // notify that there's no data
      this.noData = true;
      
      // update the map marker with a red cross icon at the point clicked
      if (this.streetviewMarker != null) {
        this.streetviewMarker.setStyle(new Style({
          image: new Icon({
            src: "assets/icons/cross.png",
            scale: 0.05,
          }),
        }));
      
        this.streetviewMarker.setGeometry(new Point(this.lastClick));
      }

      // make the street view invisible
      this._streetviewService.streetViewPanorama.setVisible(false);
    }
  };

  /**
   * function wrapper to make the search for a valid street view point synchronous
   * @param {number[]} coords coordinates in EPSG:4326 to check 
   * @returns {Promise<boolean>} true or false depending on the result of the operation
   */
  _checkAvailabilityWrapper(coords: number[]): Promise<boolean> {
    return(new Promise<boolean>((resolve, reject) => {
      this._checkAvailability(coords, resolve, reject);
    }));
  }

  /**
   * function to check if a spot specified by coords is contains information for google street view
   * @param {number[]} coords coordinates in EPSG:4326 to check 
   * @param success function to call upon success of the operation
   * @param failure function to call upon failure of the operation
   */
  async _checkAvailability(coords: number[], success, failure) {
    // try to get a panorama at the specified coordinateeeeeees
    this.googleStreetViewService.getPanorama(
      {
        location:
          {
            lat: coords[1],
            lng: coords[0]
          }
      }, 
      function (data, status) {
        if (status === 'OK') {
            // Street View panorama is available at the given coordinates
            success(true);
        } else {
            // No Street View panorama at the given coordinates
            failure(false);
        }
    });
  }

  /**
   * function that calculates the radius of effect of the click to find street view data
   * @returns {number} radius that scaled depending on the zoom level of the map, clamped within 2 numbers (max, min)
   */
  _getRadius() {
    return(Math.max(Math.min(30 * this._mapService.openLayersMap.getView().getResolution(), 80), 5));
  }
}



